import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export const ICONS = [
  '🏆', '⭐', '💎', '🔥', '🚀', '⚡', '🎯', '🏅', '🎮', '🎲',
  '♟️', '🃏', '🎳', '🏀', '⚽', '🏈', '🎱', '🎪', '👑', '🐉',
  '🦁', '🐺', '🦅', '🗡️', '🛡️', '⚔️', '🏰', '🌟', '💥', '🌈',
  '❤️', '🍀',
];

interface IconPickerProps {
  value: string;
  onChange: (value: string) => void;
  expanded: boolean;
  onToggle: () => void;
}

export const IconPickerButton: React.FC<{ value: string; onClick: () => void }> = ({ value, onClick }) => (
  <div>
    <button
      type="button"
      onClick={onClick}
      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-colors ${
        value
          ? 'border-primary-400 bg-primary-50 hover:bg-primary-100'
          : 'border-dashed border-gray-300 hover:border-primary-400 hover:bg-primary-50'
      }`}
    >
      <span className="text-xl">{value || '🏆'}</span>
    </button>
    <span className="block text-xs text-gray-500 mt-1 text-center">Icon</span>
  </div>
);

export const IconPickerCard: React.FC<{ value: string; onChange: (v: string) => void; onClose: () => void }> = ({ value, onChange, onClose }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm animate-fadeIn">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <span className="text-sm font-medium text-gray-700">Choose Icon</span>
      <div className="flex items-center gap-2">
        {value && (
          <button
            type="button"
            onClick={() => { onChange(''); onClose(); }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
    <div className="p-3 grid grid-cols-8 gap-1">
      {ICONS.map((icon) => (
        <button
          data-track="icon_option"
          key={icon}
          type="button"
          onClick={() => { onChange(icon); onClose(); }}
          className={`text-xl p-1.5 rounded transition-colors ${
            value === icon
              ? 'ring-2 ring-primary-500 bg-primary-50'
              : 'hover:bg-gray-100'
          }`}
        >
          {icon}
        </button>
      ))}
    </div>
    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fadeIn {
        animation: fadeIn 0.2s ease-out forwards;
      }
    `}</style>
  </div>
);

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, expanded, onToggle }) => (
  <div>
    <IconPickerButton value={value} onClick={onToggle} />
    {expanded && (
      <div className="mt-3">
        <IconPickerCard value={value} onChange={onChange} onClose={onToggle} />
      </div>
    )}
  </div>
);

export default IconPicker;
